import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { alpha } from "@mui/material/styles";
import { meetingPhotoDisplayUrl } from "../../api/Meetings/meetingSessionsApi";
import {
  defaultRegenMediaIds,
  isMeetingVideoPhoto,
} from "../../utils/meetingRecorder";
import MeetingVideoThumb from "./MeetingVideoThumb";

export default function MeetingRegenMediaDialog({
  open,
  photos = [],
  busy = false,
  onClose,
  onConfirm,
}) {
  const { t } = useTranslation();
  const [picked, setPicked] = useState([]);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (!open) return;
    setFilter("all");
    setPicked(defaultRegenMediaIds(photos));
    // Solo al abrir: si photos cambia a mitad, no resetear la selección.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open gate
  }, [open]);

  const visible = useMemo(() => {
    const list = Array.isArray(photos) ? photos : [];
    if (filter === "videos")
      return list.filter((photo) => isMeetingVideoPhoto(photo));
    if (filter === "photos")
      return list.filter((photo) => !isMeetingVideoPhoto(photo));
    return list;
  }, [photos, filter]);

  const toggle = (id) => {
    setPicked((prev) =>
      prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id],
    );
  };

  const selectVisible = () => {
    const ids = visible
      .map((photo) => Number(photo.id))
      .filter(Number.isFinite);
    setPicked((prev) => [...new Set([...prev, ...ids])]);
  };

  const clearVisible = () => {
    const ids = new Set(visible.map((photo) => Number(photo.id)));
    setPicked((prev) => prev.filter((id) => !ids.has(id)));
  };

  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={800}>
          {t("Meetings.regenMediaTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
          {t("Meetings.regenMediaHint")}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 1, fontWeight: 700 }}
        >
          {t("Meetings.regenMediaReplaceHint")}
        </Typography>
        <Stack
          direction="row"
          spacing={0.75}
          sx={{ mb: 1 }}
          flexWrap="wrap"
          useFlexGap
        >
          {[
            ["all", "Meetings.regenMediaFilterAll"],
            ["videos", "Meetings.regenMediaFilterVideos"],
            ["photos", "Meetings.regenMediaFilterPhotos"],
          ].map(([id, key]) => (
            <Chip
              key={id}
              size="small"
              label={t(key)}
              color={filter === id ? "primary" : "default"}
              variant={filter === id ? "filled" : "outlined"}
              onClick={() => setFilter(id)}
              sx={{ fontWeight: 700 }}
            />
          ))}
        </Stack>
        <Stack direction="row" spacing={1} sx={{ mb: 0.5 }}>
          <Button
            size="small"
            disabled={busy}
            onClick={selectVisible}
            sx={{ textTransform: "none", fontWeight: 700, minWidth: 0, px: 1 }}
          >
            {t("Meetings.regenMediaSelectAll")}
          </Button>
          <Button
            size="small"
            disabled={busy}
            onClick={clearVisible}
            sx={{ textTransform: "none", fontWeight: 700, minWidth: 0, px: 1 }}
          >
            {t("Meetings.regenMediaSelectNone")}
          </Button>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ alignSelf: "center" }}
          >
            {picked.length}
          </Typography>
        </Stack>
        <Box
          sx={{
            maxHeight: 360,
            overflow: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          {visible.map((photo) => {
            const id = Number(photo.id);
            const checked = picked.includes(id);
            const video = isMeetingVideoPhoto(photo);
            const missing = video
              ? !String(photo.teamNote || "").trim()
              : !(photo.hasVision || String(photo.visionText || "").trim());
            return (
              <Stack
                key={id}
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => !busy && toggle(id)}
                sx={{
                  px: 0.5,
                  py: 0.5,
                  cursor: busy ? "default" : "pointer",
                  "&:hover": {
                    bgcolor: (th) => alpha(th.palette.text.primary, 0.04),
                  },
                }}
              >
                <Checkbox
                  size="small"
                  checked={checked}
                  tabIndex={-1}
                  disableRipple
                  disabled={busy}
                />
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 1,
                    overflow: "hidden",
                    flexShrink: 0,
                    bgcolor: "action.hover",
                  }}
                >
                  {video ? (
                    <MeetingVideoThumb
                      fileUrl={photo.fileUrl}
                      sx={{ width: 48, height: 48 }}
                    />
                  ) : (
                    <Box
                      component="img"
                      alt=""
                      src={meetingPhotoDisplayUrl(photo)}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  )}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" fontWeight={700}>
                    {video
                      ? t("Meetings.regenMediaVideo")
                      : t("Meetings.regenMediaPhoto")}{" "}
                    {photo.offsetLabel || `#${id}`}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={missing ? "warning.main" : "text.secondary"}
                  >
                    {video
                      ? missing
                        ? t("Meetings.regenMediaMissingNote")
                        : t("Meetings.regenMediaHasNote")
                      : missing
                        ? t("Meetings.regenMediaMissingRead")
                        : t("Meetings.regenMediaHasRead")}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
        </Box>
        {!picked.length ? (
          <Typography
            variant="caption"
            color="error"
            sx={{ display: "block", mt: 0.75, fontWeight: 700 }}
          >
            {t("Meetings.regenMediaNeedOne")}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ textTransform: "none", fontWeight: 700 }}
        >
          {t("Meetings.cancel")}
        </Button>
        <Button
          variant="contained"
          disabled={busy || !picked.length}
          onClick={() => onConfirm?.(picked)}
          startIcon={
            busy ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AutoAwesomeOutlinedIcon />
            )
          }
          sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
        >
          {busy
            ? t("Meetings.regenMediaWorking")
            : t("Meetings.regenMediaSubmit", { n: picked.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
